import { graphqlWithAuth, fetchWithAuth } from "../utils/fetchWithAuth";

export const ALL_ACTIVE_DIVISIONS_QUERY = `
  query AllActiveDivisions {
    allActiveDivisions {
      id
      name
    }
  }
`;

/**
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export async function fetchAllActiveDivisions() {
  const data = await graphqlWithAuth({ query: ALL_ACTIVE_DIVISIONS_QUERY });
  const list = data?.allActiveDivisions;
  return Array.isArray(list) ? list : [];
}

export const ALL_ACTIVE_RESTRICTIONS_QUERY = `
  query {
    allActiveRestrictions {
      id
      name
    }
  }
`;

/**
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export async function fetchAllActiveRestrictions() {
  const data = await graphqlWithAuth({ query: ALL_ACTIVE_RESTRICTIONS_QUERY });
  const list = data?.allActiveRestrictions;
  return Array.isArray(list) ? list : [];
}

export const ALL_ACTIVE_ATTRIBUTES_QUERY = `
  query {
    allActiveAttributes {
      id
      name
    }
  }
`;

/**
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export async function fetchAllActiveAttributes() {
  const data = await graphqlWithAuth({ query: ALL_ACTIVE_ATTRIBUTES_QUERY });
  const list = data?.allActiveAttributes;
  return Array.isArray(list) ? list : [];
}

export const SEARCH_PARTS_QUERY = `
  query SearchParts(
    $div: String
    $pls: String
    $part: String
    $itemId: String
    $description: String
    $partAddDate: String
    $availability: String
    $restriction: String
    $attribute: String
  ) {
    searchParts(
      div: $div
      pls: $pls
      part: $part
      itemId: $itemId
      description: $description
      partAddDate: $partAddDate
      availability: $availability
      restriction: $restriction
      attribute: $attribute
    ) {
      partNumber
      partName
      div
      pls
      itemId
      partAddDate
      availability {
        code
      }
      attributes {
        attributeId
        name
        value
        uom
      }
      restrictions {
        restrictionId
        name
        includeExcludeFlag
      }
    }
  }
`;

/**
 * @param {{
 *   div?: string | null
 *   pls?: string | null
 *   part?: string | null
 *   itemId?: string | null
 *   description?: string | null
 *   partAddDate?: string | null
 *   availability?: string | null
 *   restriction?: string | null
 *   attribute?: string | null
 * }} variables
 * @returns {Promise<Array<object>>}
 */
export async function fetchSearchParts(variables) {
  const data = await graphqlWithAuth({
    query: SEARCH_PARTS_QUERY,
    variables,
    operationName: "SearchParts",
  });
  const raw = data?.searchParts;
  if (Array.isArray(raw)) return raw;
  if (raw != null && typeof raw === "object") return [raw];
  return [];
}

export const GET_PART_DETAILS_BY_ITEM_ID_QUERY = `
  query GetPartDetailsByItemId($itemId: String!) {
    getPartDetailsByItemId(itemId: $itemId) {
      itemId
      div
      pls
      partNumber
      description
      partRank
      addedDate
      availability
      partPrice
      son
      imageUrl
      divisionDescription
      accessoryFlag
      upc
      partSourceCode
      routeCode
    }
  }
`;

/**
 * Single record for part details header / summary.
 * @param {string} itemId
 * @returns {Promise<object | null>}
 */
export async function fetchPartDetailsByItemId(itemId) {
  const data = await graphqlWithAuth({
    query: GET_PART_DETAILS_BY_ITEM_ID_QUERY,
    variables: { itemId },
    operationName: "GetPartDetailsByItemId",
  });
  const raw = data?.getPartDetailsByItemId;
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw[0] ?? null;
  if (typeof raw === "object") return raw;
  return null;
}

export const GET_ATTRIBUTES_BY_ITEM_ID_QUERY = `
  query GetAttributesByItemId($itemId: String!) {
    getAttributesByItemId(itemId: $itemId) {
      id
      name
    }
  }
`;

/**
 * @param {string} itemId
 * @returns {Promise<Array<{ id: string, name: string }>>}
 */
export async function fetchAttributesByItemId(itemId) {
  const data = await graphqlWithAuth({
    query: GET_ATTRIBUTES_BY_ITEM_ID_QUERY,
    variables: { itemId },
    operationName: "GetAttributesByItemId",
  });
  const raw = data?.getAttributesByItemId;
  if (Array.isArray(raw)) return raw;
  if (raw != null && typeof raw === "object") return [raw];
  return [];
}

export const GET_PART_ATTRIBUTES_BY_ITEM_ID_QUERY = `
  query GetPartAttributesByItemId($itemId: String!) {
    getPartAttributesByItemId(itemId: $itemId) {
      attributeId
      attributeName
      value
      uom
    }
  }
`;

/**
 * Part-level attribute values for an item (detail screen).
 * @param {string} itemId
 * @returns {Promise<Array<{ attributeId: string, attributeName: string, value: string, uom: string }>>}
 */
export async function fetchPartAttributesByItemId(itemId) {
  const data = await graphqlWithAuth({
    query: GET_PART_ATTRIBUTES_BY_ITEM_ID_QUERY,
    variables: { itemId },
    operationName: "GetPartAttributesByItemId",
  });
  const raw = data?.getPartAttributesByItemId;
  if (Array.isArray(raw)) return raw;
  if (raw != null && typeof raw === "object") return [raw];
  return [];
}

export const GET_PART_RESTRICTIONS_BY_ITEM_ID_QUERY = `
  query GetPartRestrictionsByItemId($itemId: String!) {
    getPartRestrictionsByItemId(itemId: $itemId) {
      restrictionId
      restrictionName
      restrictionIncludeExclude
    }
  }
`;

/** GraphQL may return INTERNAL_ERROR when a part has no restrictions to load. */
export function isRestrictionsNoDataError(err) {
  const msg = String(err?.message ?? "").trim().toUpperCase();
  return msg === "INTERNAL_ERROR" || msg.includes("INTERNAL_ERROR");
}

/**
 * @param {string} itemId
 * @returns {Promise<Array<{ restrictionId: string, restrictionName: string, restrictionIncludeExclude: string }>>}
 */
export async function fetchPartRestrictionsByItemId(itemId) {
  try {
    const data = await graphqlWithAuth({
      query: GET_PART_RESTRICTIONS_BY_ITEM_ID_QUERY,
      variables: { itemId },
      operationName: "GetPartRestrictionsByItemId",
    });
    const raw = data?.getPartRestrictionsByItemId;
    if (Array.isArray(raw)) return raw;
    if (raw != null && typeof raw === "object") return [raw];
    return [];
  } catch (e) {
    if (isRestrictionsNoDataError(e)) return [];
    throw e;
  }
}

/**
 * DELETE restriction (REST). Query: username, partId, restrictionId only.
 * @param {{ username: string, partId: string, restrictionId: string }} args
 */
export async function deletePartCatalogRestriction({
  username,
  partId,
  restrictionId,
}) {
  return fetchWithAuth({
    method: "PUT",
    endpoint: "/part-catalog/restriction/deleteRestriction",
    params: {
      username: String(username ?? "").trim(),
      partId: String(partId ?? "").trim(),
      restrictionId: String(restrictionId ?? "").trim(),
    },
  });
}

/**
 * Delete a part attribute (REST).
 * PUT …/delete?username=&partId=&attributeId=
 * @param {{ username: string, partId: string, attributeId: string }} args
 */
export async function deletePartCatalogAttribute({
  username,
  partId,
  attributeId,
}) {
  return fetchWithAuth({
    method: "PUT",
    endpoint: "/part-catalog/attribute/delete",
    params: {
      username: String(username ?? "").trim(),
      partId: String(partId ?? "").trim(),
      attributeId: String(attributeId ?? "").trim(),
    },
  });
}

/**
 * Update part attribute value and UOM (REST).
 * PUT …/update?username=&partId=&attributeId=&value=&uom=
 * @param {{
 *   username: string
 *   partId: string
 *   attributeId: string
 *   value: string
 *   uom: string
 * }} args
 */
export async function updatePartCatalogAttribute({
  username,
  partId,
  attributeId,
  value,
  uom,
}) {
  return fetchWithAuth({
    method: "PUT",
    endpoint: "/part-catalog/attribute/update",
    params: {
      username: String(username ?? "").trim(),
      partId: String(partId ?? "").trim(),
      attributeId: String(attributeId ?? "").trim(),
      value: value != null ? String(value) : "",
      uom: uom != null ? String(uom) : "",
    },
  });
}

/**
 * Add an attribute to a part (REST).
 * POST …/attribute/add?username=&partId=&attributeId=&value=&uom=
 * @param {{
 *   username: string
 *   partId: string
 *   attributeId: string
 *   value: string
 *   uom: string
 * }} args
 */
export async function addPartCatalogAttribute({
  username,
  partId,
  attributeId,
  value,
  uom,
}) {
  return fetchWithAuth({
    method: "POST",
    endpoint: "/attribute/add",
    params: {
      username: String(username ?? "").trim(),
      partId: String(partId ?? "").trim(),
      attributeId: String(attributeId ?? "").trim(),
      value: value != null ? String(value) : "",
      uom: uom != null ? String(uom) : "",
    },
  });
}

/**
 * Update restriction include/exclude flag (REST).
 * PUT …/includeExcludeFlag?username=&partId=&restrictionId=&includeExcludeFlag=
 * @param {{ username: string, partId: string, restrictionId: string, includeExcludeFlag: string }} args
 */
export async function updatePartCatalogRestrictionIncludeExclude({
  username,
  partId,
  restrictionId,
  includeExcludeFlag,
}) {
  return fetchWithAuth({
    method: "PUT",
    endpoint: "/part-catalog/restriction/includeExcludeFlag",
    params: {
      username: String(username ?? "").trim(),
      partId: String(partId ?? "").trim(),
      restrictionId: String(restrictionId ?? "").trim(),
      includeExcludeFlag: String(includeExcludeFlag ?? "").trim(),
    },
  });
}

/**
 * Add a restriction to a part (REST).
 * POST …/add?username=&partId=&restrictionId=&includeExcludeFlag=
 * @param {{
 *   username: string
 *   partId: string
 *   restrictionId: string
 *   includeExcludeFlag: string
 * }} args
 */
export async function addPartCatalogRestriction({
  username,
  partId,
  restrictionId,
  includeExcludeFlag,
}) {
  return fetchWithAuth({
    method: "POST",
    endpoint: "/part-catalog/restriction/add",
    params: {
      username: String(username ?? "").trim(),
      partId: String(partId ?? "").trim(),
      restrictionId: String(restrictionId ?? "").trim(),
      includeExcludeFlag: String(includeExcludeFlag ?? "").trim(),
    },
  });
}

export const GET_SUBSTITUTE_PARTS_BY_ITEM_ID_QUERY = `
  query GetSubstitutePartsByItemId($itemId: String!) {
    getSubstitutePartsByItemId(itemId: $itemId) {
      subItemId
      subPartNumber
      subDescription
      subDivision
      subPls
      subPartAddDate
      subAvailability
      subPrice
      subProductGroupDescription
      subImageUrl
    }
  }
`;

/**
 * @param {string} itemId
 * @returns {Promise<Array<object>>}
 */
export async function fetchSubstitutePartsByItemId(itemId) {
  const data = await graphqlWithAuth({
    query: GET_SUBSTITUTE_PARTS_BY_ITEM_ID_QUERY,
    variables: { itemId },
    operationName: "GetSubstitutePartsByItemId",
  });
  const raw = data?.getSubstitutePartsByItemId;
  if (Array.isArray(raw)) return raw;
  if (raw != null && typeof raw === "object") return [raw];
  return [];
}

/**
 * Upload a parts-catalog bulk file to S3 via the DB Editor API.
 * POST multipart/form-data …/images/upload
 * form-data: file (File), key (e.g. "bulk-upload/title/PartTitle_12312025.txt")
 * @param {{ file: File, uploadType: string }} args
 */
export async function uploadPartsCatalogBulkFile({ file, uploadType }) {
  const typePrefix = String(uploadType ?? "").trim();
  const fileName = file?.name != null ? String(file.name) : "";
  const key = typePrefix && fileName ? `${typePrefix}/${fileName}` : fileName;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("key", key);

  return fetchWithAuth({
    method: "POST",
    endpoint: "/images/upload",
    data: formData,
  });
}
